import { Component, computed, inject, OnInit, resource, signal } from '@angular/core';
import { FullscreenLayout } from '../../../layout/components/fullscreen-layout/fullscreen-layout';
import { PageHeader } from '../../../layout/components/page-header/page-header';
import { PageTitle } from '../../../layout/components/page-title/page-title';
import { Api } from '../../../../core/clients/omni-core/api';
import {
  listAllParseRequests,
  ListAllParseRequests$Params,
} from '../../../../core/clients/omni-core/functions';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { form, FormField } from '@angular/forms/signals';
import { Select, Badge, LoadingSpinner, TooltipDirective, Button, Skeleton } from 'excellence-ui';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { BrokerParseRequestsTableFilter } from './definitions';
import { TranslocoPipe } from '@jsverse/transloco';
import { FeedbackFab } from '../../../monitoring/components/feedback-fab/feedback-fab';
import { TechnicalMonitoringEnabledDirective } from '../../../monitoring/directives/technical-monitoring-enabled.directive';
import { NgIcon } from '@ng-icons/core';
import { OrganizationNamePipe } from '../../../organizations/pipes/organization-name-pipe';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';

@Component({
  selector: 'app-broker-parse-requests-page',
  imports: [
    FullscreenLayout,
    PageHeader,
    PageTitle,
    DatePipe,
    RouterLink,
    FormField,
    Select,
    Badge,
    ErrorPage,
    LoadingSpinner,
    TranslocoPipe,
    FeedbackFab,
    TechnicalMonitoringEnabledDirective,
    TooltipDirective,
    Button,
    NgIcon,
    OrganizationNamePipe,
    Skeleton,
  ],
  templateUrl: './broker-parse-requests-page.html',
})
export class BrokerParseRequestsPage implements OnInit {
  private api = inject(Api);
  private activatedRoute = inject(ActivatedRoute);
  protected router = inject(Router);
  private organizationsStore = inject(OrganizationsStore);

  private tableFilter = signal<BrokerParseRequestsTableFilter>({
    limit: '20',
    page: '1',
    status: 'ALL',
  });

  private limit = computed(() => +this.tableFilter().limit);
  private page = computed(() => +this.tableFilter().page);
  protected total = computed(() => this.value()?.total ?? 0);
  protected status = computed(() => this.tableFilter().status);
  protected value = computed(() => {
    if (this.parseRequests.hasValue()) {
      return this.parseRequests.value();
    }
    return null;
  });

  protected tableForm = form(this.tableFilter);

  protected pageOptions = computed(() => {
    if (!this.parseRequests.hasValue()) {
      return null;
    }
    const total = this.total();
    const pageSize = this.limit();
    const options = [];
    const totalPages = Math.max(Math.ceil(total / pageSize), this.page());
    for (let i = 1; i <= totalPages; i++) {
      options.push(i.toString());
    }
    return options;
  });

  protected parseRequests = resource({
    params: () => ({
      limit: this.limit(),
      offset: (this.page() - 1) * this.limit(),
      status:
        this.status() === 'ALL'
          ? undefined
          : (this.status() as ListAllParseRequests$Params['status']),
    }),
    loader: ({ params }) => this.api.invoke(listAllParseRequests, params),
  });

  protected organizationsLoading = this.organizationsStore.allLoading;
  protected organizationsLoadingError = this.organizationsStore.allLoadingError;

  ngOnInit(): void {
    const page = this.activatedRoute.snapshot.queryParamMap.get('page');
    const limit = this.activatedRoute.snapshot.queryParamMap.get('limit');
    const status = this.activatedRoute.snapshot.queryParamMap.get('status');
    this.tableFilter.update((v) => ({
      page: page ?? v.page,
      limit: limit ?? v.limit,
      status: (status as BrokerParseRequestsTableFilter['status']) ?? v.status,
    }));
  }

  protected onChangeFilter() {
    const { page, limit, status } = this.tableFilter();
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        page: page,
        limit: limit,
        status: status === 'ALL' ? null : status,
      },
      queryParamsHandling: 'replace',
    });
  }
}
